import { partition } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
    phfOrderMasterMessageHub as messageHub,
    PushCode,
    NewPhfOrderPushData,
    PushOrderUserRole,
    getWmPoiId,
    canBeHandledByCurrentPoi,
} from '@services/messageHub/instance';
import { recordReceiptInfo } from '@utils/report/orderReceiptReport';
import * as orderReport from '@utils/report/orderArrivalRateReport';
import { ifPoiEnableAutoAcceptLocally } from '@utils/poiHelper';
import { reportPhfPushMessageReceive } from '@utils/phf/PhfExposure/phfMessageCustomMetric';
import { UnprocessedOrderType } from '@business/pages/OrderManage/shared/types';
import {
    reportOrderFingerprint,
    PC_ORDER_FINGERPRINT_ID,
} from '@utils/report/orderFingerprintReport';

import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';

import { confirmPhfOrderAndPrint } from '../../connection/eventHandler/order/phfOrder';

import { flashWindow, updateOrderCount } from '../../connection/eventHandler/order/order';
import { addOrderNotification } from '../../connection/eventHandler/pushNotification';
import bellService from '../../service/bell';
import { FRAME_NAME, sendMessageToSubFrame } from '../../utils/subFrames';

export default () => {
    const [autoAcceptPoiOrder$, noneAutoAcceptPoiOrder$] = partition(
        messageHub.getPushSource<NewPhfOrderPushData>(PushCode.NEW_ORDER_1),
        ({ origin: message }) => ifPoiEnableAutoAcceptLocally(getWmPoiId(message)),
    );

    const reportNewOrderDetailFingerprint = {
        success: (orderViewId: string, orderPoiId: string) => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                0,
            );
        },
        fail: (orderViewId: string, orderPoiId: string) => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                1,
            );
        },
    };

    // 自动接单门店订单处理流程
    autoAcceptPoiOrder$.subscribe(({ origin: message, detail }) => {
        // 订单指纹上报
        try {
            reportOrderFingerprint(
                message.wmOrderViewId,
                getWmPoiId(message),
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_PUSH_ARRIVE,
                0,
            );
            reportPhfPushMessageReceive(PushCode.NEW_ORDER_1, {
                message,
                tags: {
                    autoAccept: 1,
                },
            });
        } catch (e) {
            // let it go
        }

        // 拼单一起送子单仅上报新订单到达指纹
        if (message.user_role === PushOrderUserRole.PHF_SUB) {
            return;
        }

        // 上报新订单详情指纹
        if (!detail) {
            reportNewOrderDetailFingerprint.fail(message.wmOrderViewId, getWmPoiId(message));
            return;
        }
        reportNewOrderDetailFingerprint.success(message.wmOrderViewId, getWmPoiId(message));

        const {
            businessType,
            orderInfo: { wm_poi_id, wm_order_id_view_str, orderType },
        } = detail;

        orderReport.reportEvent(orderReport.ORDER_ARRIVE_EVENT.PUSH_ARRIVED, [
            wm_order_id_view_str,
        ]);

        // 接单并打印
        confirmPhfOrderAndPrint(
            {
                wmPoiId: wm_poi_id,
                wmOrderViewId: wm_order_id_view_str,
                businessType,
                orderType,
            },
            'auto_push',
        );

        // 响铃
        if (message.isPreOrder) {
            bellService.playBell('autonewpreorder');
        } else {
            bellService.playBell('autoneworder');
        }

        // 订单消息
        addOrderNotification({
            content: '美团外卖自动接单，刚刚为您接到新订单了',
            href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                ? '/new_fe/orderbusiness#/order/unprocessed?tab=new'
                : '/new_fe/order#/order/unprocessed?tab=newOrder',
            wmPoiId: `${wm_poi_id}`,
        });

        // 订单回执
        recordReceiptInfo(
            [
                {
                    wmPoiId: `${wm_poi_id}`,
                    wmOrderViewId: wm_order_id_view_str,
                },
            ],
            true,
        );
        // 订单到达率上报
        orderReport.reportEvent(orderReport.ORDER_ARRIVE_EVENT.PUSH_DETAIL_SUCCESS, [
            wm_order_id_view_str,
        ]);
        // 唤起客户端或闪烁图标
        flashWindow();
    });

    // 非自动接单门店订单处理流程
    noneAutoAcceptPoiOrder$.subscribe(({ origin: message }) => {
        try {
            reportPhfPushMessageReceive(PushCode.NEW_ORDER_1, {
                message,
                tags: {
                    autoAccept: 0,
                },
            });
        } catch (e) {
            // console.log(e)
        }
        orderReport.reportEvent(orderReport.ORDER_ARRIVE_EVENT.PUSH_ARRIVED, [
            message.wmOrderViewId,
        ]);
        updateOrderCount({
            [UnprocessedOrderType.NewOrder]: '+1',
            noticeInner: true,
        });
        // 履约新形态
        // 待接单
        updateOrderCount({
            [UnprocessedOrderType.New]: '+1',
            noticeInner: true,
        });
        // 新形态
        updateOrderCount({
            [UnprocessedOrderType.All]: '1',
            noticeInner: true,
        });
        if (message.isPreOrder) {
            bellService.playBell('newpreorder');
        } else {
            bellService.playBell('neworder');
        }
        addOrderNotification({
            content: '您有新订单了',
            href: '/new_fe/business#/order/unprocessed?tab=newOrder',
            wmPoiId: getWmPoiId(message),
        });
        flashWindow();
    });

    // Code:57 拼好饭拼单客服赔付状态变更
    messageHub
        .getPushSource(PushCode.PHF_COMBINE_CS_COMPENSATE_STATUS_CHANGE_57)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            reportPhfPushMessageReceive(PushCode.PHF_COMBINE_CS_COMPENSATE_STATUS_CHANGE_57, {
                tags: { orderViewId: message.wm_order_view_id },
            });
        });

    // Code:121 预售单新订单
    messageHub
        .getPushSource(PushCode.PREPARATION_NEW_ORDER_121)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            reportPhfPushMessageReceive(PushCode.PREPARATION_NEW_ORDER_121, { message });
            bellService.playBell('preparationOrderNew');
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:122 预售单待出餐
    messageHub
        .getPushSource(PushCode.PREPARATION_PREP_MEAL_ORDER_122)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            reportPhfPushMessageReceive(PushCode.PREPARATION_PREP_MEAL_ORDER_122, { message });
            bellService.playBell('preparationOrderPrepMeal');
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:123 预售单取消单
    messageHub
        .getPushSource(PushCode.PREPARATION_CANCEL_ORDER_123)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            reportPhfPushMessageReceive(PushCode.PREPARATION_CANCEL_ORDER_123, { message });
            bellService.playBell('preparationOrderCancel');
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });
};
